import * as yup from "yup";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";

import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import LoadingButton from "@mui/lab/LoadingButton";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import { Box, TextareaAutosize } from "@mui/material";

import Title from "../../../common/Title";
import InputFieldBox from "../../../common/InputFieldBox";
import useAlert from "../../../../hooks/useAlert";
import useAxios from "../../../../hooks/useAxios";
import { __activateNyx } from "../../../../api/sim";

const ActivateForm = () => {
  const [rawData, setRawData] = useState("");
  const [areaCodes, setAreaCodes] = useState([]);
  const [formData, setFormData] = useState(null);
  const { setAlertMessage } = useAlert();
  const [response, call, loading] = useAxios({ ...__activateNyx });
  const nav = useNavigate();

  const initialData = { requested_lines: 0 };

  const formik = useFormik({
    initialValues: { ...initialData },
    validationSchema: yup.object({
      requested_lines: yup
        .number("requested_lines")
        .required("Requested lines is required")
        .min(1, "Requested lines must be at least 1")
        .integer("Requested lines must be a whole number"),
    }),
    onSubmit: (values) => {
      setFormData({
        requested_lines: Number(values.requested_lines),
        ...(areaCodes.length ? { area_codes: areaCodes } : {}),
      });
    },
    validateOnChange: true,
  });

  const validateAreaCodes = (data, requestedLines) => {
    const errors = {};
    if (!data || !data.trim()) {
      setAreaCodes([]);
      return errors;
    }

    const lines = data
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);

    if (requestedLines && lines.length > requestedLines) {
      errors.area_codes = `Number of area codes (${lines.length}) cannot exceed requested lines (${requestedLines})`;
      return errors;
    }

    for (let i = 0; i < lines.length; i += 1) {
      if (!/^\d{3}$/.test(lines[i])) {
        errors.area_codes = `Invalid format in line ${i + 1}: ${lines[i]}`;
        return errors;
      }
    }

    setAreaCodes(lines);
    return errors;
  };

  useEffect(() => {
    if (!formData) return;
    call({ data: formData });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  useEffect(() => {
    if (!response) return;
    if (response.status) {
      setAlertMessage({ type: "success", message: "Activation queue started" });
      nav("/nyx/activate");
    }
    formik.setSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response]);

  if (loading) {
    return <Title content="Please wait..." variant="subtitle2" gutterBottom />;
  }

  return (
    <Box sx={{ width: "100%" }} display="flex" alignItems="center" flexDirection="column">
      <Title variant="h5" content="Activate new numbers (Nyx)" sx={{ mb: 3 }} gutterBottom />
      <Grid
        sx={{ flexGrow: 1 }}
        container
        spacing={2}
        direction="column"
        justifyContent="center"
        alignItems="center"
      >
        <Grid item xs={12}>
          <FormControl error={Boolean(formik.errors.requested_lines)} sx={{ width: "300px" }}>
            <InputFieldBox
              value={formik.values.requested_lines}
              onChange={(event) => {
                const value = event.target.value;
                formik.setFieldValue("requested_lines", value);
                const errs = validateAreaCodes(rawData, value ? parseInt(value, 10) : 0);
                formik.setErrors({ ...formik.errors, area_codes: errs.area_codes });
              }}
              label="Requested Lines"
              type="number"
              placeholder="Enter number of lines to activate"
            />
            {Boolean(formik.errors.requested_lines) ? (
              <FormHelperText id="requested_lines-error-text">{formik.errors.requested_lines}</FormHelperText>
            ) : (
              ""
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <InputLabel htmlFor="area_codes">
            {"Add area codes (optional, one per line):"}
            <br />
            {"212"}
            <br />
            {"415"}
            <br />
            <em>{"Area codes must be 3 digits and cannot exceed requested lines."}</em>
          </InputLabel>
          <FormControl error={Boolean(formik.errors.area_codes)} sx={{ width: "100%" }}>
            <TextareaAutosize
              multiline
              rows={6}
              aria-label="minimum height"
              placeholder={"Add area codes (optional)\n212\n415"}
              style={{ minWidth: 550 }}
              minRows={5}
              name="area_codes"
              id="area_codes"
              value={rawData}
              onChange={(e) => {
                const data = e.target.value;
                setRawData(data);
                const errs = validateAreaCodes(data, formik.values.requested_lines);
                formik.setErrors({ ...formik.errors, area_codes: errs.area_codes });
              }}
            />
            {Boolean(formik.errors.area_codes) ? (
              <FormHelperText id="area_codes-error-text">{formik.errors.area_codes}</FormHelperText>
            ) : (
              ""
            )}
          </FormControl>
        </Grid>
        <Grid container sx={{ mt: 3 }} direction="row" justifyContent="center" alignItems="center">
          <Grid item>
            <LoadingButton
              style={{ marginRight: "5px" }}
              disabled={
                formik.errors.requested_lines ||
                !formik.values.requested_lines ||
                formik.errors.area_codes
              }
              onClick={() => !loading && !formik.isSubmitting && formik.submitForm()}
              size="large"
              loading={loading}
              variant="contained"
            >
              {"Activate Numbers"}
            </LoadingButton>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ActivateForm;
